// Top-bar comms button with unread/live indicator. Opens a flyout
// with two tabs: "Now" (focused thread for the current selection or a
// placeholder when nothing's selected) and "Sector" (live chatter
// feed). Broadcast composer at the bottom.
//
// Lives in the ContextBar so it's always one click away.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, TextField, Tooltip } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import { SECTOR_CHATTER, threadFor } from './mockComms';
import { useIntel } from './IntelligenceContext';
import { C } from '../styles';

function CommsBell() {
  const intel = useIntel();
  const { selected, setSelected } = intel;

  const [open, setOpen] = useState(false);
  const [tab, setTab]   = useState('now');   // 'now' | 'sector'
  const [scope, setScope] = useState('all'); // broadcast scope
  const [draft, setDraft] = useState('');
  const wrapperRef = useRef(null);

  // Auto-switch to "now" when something is newly selected.
  useEffect(() => {
    if (selected) setTab('now');
  }, [selected]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const focusedThread = useMemo(() => threadFor(selected), [selected]);

  const handleSend = () => {
    if (!draft.trim()) return;
    setDraft('');
    // Demo only — would call out to a real API.
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Tooltip title="Comms">
        <IconButton
          size="small"
          onClick={() => setOpen(o => !o)}
          sx={{
            color: C.textSecondary,
            backgroundColor: open ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
          }}
        >
          <ForumIcon sx={{ fontSize: 20 }} />
          {/* Live indicator dot */}
          <Box sx={{
            position: 'absolute',
            top: 6, right: 6,
            width: 7, height: 7, borderRadius: '50%',
            backgroundColor: '#4CAF50',
            border: '1.5px solid #0d1019',
            animation: 'commsPulse 1.6s ease-in-out infinite',
            '@keyframes commsPulse': {
              '0%, 100%': { opacity: 1 },
              '50%':      { opacity: 0.35 },
            },
          }} />
        </IconButton>
      </Tooltip>

      {open && (
        <Box sx={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          width: 380,
          height: 540,
          zIndex: 30,
          backgroundColor: 'rgba(13, 16, 25, 0.97)',
          border: `1px solid ${C.border}`,
          borderRadius: 1,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.6)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Tabs */}
          <Box sx={{
            display: 'flex', borderBottom: `1px solid ${C.border}`,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }}>
            {[
              { key: 'now',    label: focusedThread ? `Now · ${focusedThread.title.replace(/^Thread · /, '')}` : 'Now' },
              { key: 'sector', label: 'Sector' },
            ].map(t => (
              <Box
                key={t.key}
                onClick={() => setTab(t.key)}
                sx={{
                  flex: 1,
                  px: 1.25, py: 1,
                  cursor: 'pointer',
                  fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: tab === t.key ? C.accent : C.textMuted,
                  borderBottom: `2px solid ${tab === t.key ? C.accent : 'transparent'}`,
                  textAlign: 'center',
                  '&:hover': { color: C.textSecondary },
                }}
              >
                <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit', color: 'inherit' }}>
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* List */}
          <Box sx={{
            flex: 1, overflowY: 'auto', minHeight: 0,
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 3 },
          }}>
            {tab === 'now' ? (
              focusedThread ? (
                <>
                  <Box sx={{
                    px: 1.25, py: 0.75,
                    borderBottom: `1px solid ${C.border}`,
                    backgroundColor: 'rgba(0,0,0,0.18)',
                  }}>
                    <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                      {focusedThread.subtitle}
                    </Typography>
                  </Box>
                  {focusedThread.messages.map(m => (
                    <MessageRow key={m.id} message={m} setSelected={setSelected} />
                  ))}
                </>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, fontStyle: 'italic' }}>
                    Select a driver, gate, or flight to see its thread.
                  </Typography>
                </Box>
              )
            ) : (
              SECTOR_CHATTER.map(m => (
                <MessageRow key={m.id} message={m} setSelected={setSelected} />
              ))
            )}
          </Box>

          {/* Composer */}
          <Box sx={{
            px: 1.25, py: 0.85,
            borderTop: `1px solid ${C.border}`,
            backgroundColor: 'rgba(0,0,0,0.25)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Send to
              </Typography>
              {[
                { key: 'all',    label: 'All' },
                { key: 'sector', label: 'Sector' },
                { key: 'driver', label: 'Driver' },
              ].map(s => (
                <Box
                  key={s.key}
                  onClick={() => setScope(s.key)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.55rem', fontWeight: 700, letterSpacing: 0.5,
                    px: 0.55, py: 0.15,
                    borderRadius: 0.5,
                    color: scope === s.key ? C.accent : C.textMuted,
                    backgroundColor: scope === s.key ? 'rgba(249, 182, 18, 0.12)' : 'transparent',
                    border: `1px solid ${scope === s.key ? C.accent : 'transparent'}`,
                    textTransform: 'uppercase',
                  }}
                >
                  {s.label}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
                placeholder="Type a message…"
                size="small" fullWidth multiline maxRows={3}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'rgba(0,0,0,0.35)', color: C.textPrimary,
                    fontSize: '0.75rem', py: 0.5, px: 1,
                  },
                  '& fieldset': { borderColor: C.border },
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: C.accent },
                }}
              />
              <IconButton
                onClick={handleSend}
                disabled={!draft.trim()}
                sx={{
                  color: draft.trim() ? '#000' : C.textMuted,
                  backgroundColor: draft.trim() ? C.accent : 'transparent',
                  border: `1px solid ${draft.trim() ? C.accent : C.border}`,
                  borderRadius: 0.75, p: 0.65,
                  '&:hover': { filter: 'brightness(1.1)' },
                }}
              >
                <SendIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function MessageRow({ message, setSelected }) {
  const a = message.author;
  const isDispatch = a.kind === 'dispatch';
  const isSystem   = a.kind === 'system';
  const accent = isDispatch ? C.accent : (isSystem ? '#00BCD4' : '#94A3B8');

  return (
    <Box
      onClick={() => a.kind === 'driver' && setSelected({ kind: 'driver', id: a.id })}
      sx={{
        px: 1.25, py: 0.75,
        borderBottom: `1px solid ${C.border}`,
        cursor: a.kind === 'driver' ? 'pointer' : 'default',
        '&:hover': a.kind === 'driver' ? { backgroundColor: 'rgba(255, 255, 255, 0.03)' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.2 }}>
        <Typography sx={{
          fontSize: '0.66rem', fontWeight: 700, color: accent, letterSpacing: 0.3,
        }}>
          {a.name}
        </Typography>
        {a.kind === 'driver' && (
          <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 400 }}>
            #{a.badge}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
          {message.time}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, lineHeight: 1.35 }}>
        {message.body}
      </Typography>
    </Box>
  );
}

export default CommsBell;
